import { Button } from "#components/button";
import { Modal } from "#components/modal";
import {
  Alert,
  AlertTitle,
  Collapse,
  DialogActions,
  FormControl,
  FormHelperText,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutline";
import { useMutation } from "@tanstack/react-query";
import {
  AsYouType,
  isValidPhoneNumber,
  parsePhoneNumberWithError,
} from "@dmca/utils/libphonenumber";
import { useState, useMemo, useEffect, forwardRef } from "react";
import { Input, type InputProps } from "./Input";
import { useCountdownTimer } from "#hooks/useCountdownTimer";
import { useTRPC } from "#trpc";
import { mergeSx } from "#hooks/mergeSx";

export interface PhoneInputProps
  extends Omit<InputProps, "onChange" | "value"> {
  /** The current phone number value and verification status */
  value?: { value: string; isVerified: boolean };
  /** Callback when the phone number changes */
  onChange: (data: { value: string; isVerified: boolean }) => void;
  /** Whether verification is required (defaults to true) */
  needsVerification?: boolean;
  /** Invite credentials for verification before account creation. */
  invite?: { code: string; token: string };
}

/**
 * PhoneInput Component
 *
 * A specialized phone input component that includes optional phone number verification functionality.
 *
 * Features:
 * - Automatic phone number formatting using libphonenumber
 * - Real-time validation of phone numbers
 * - Optional verification: Verify button that slides in when a valid phone number is entered
 * - Verification modal with code input
 * - Countdown timer for resend functionality
 * - Uses custom Input component as base
 *
 * @example
 * ```tsx
 * // With verification (default)
 * const [phoneData, setPhoneData] = useState({ value: "", isVerified: false });
 *
 * <PhoneInput
 *   label="Phone Number"
 *   value={phoneData}
 *   onChange={setPhoneData}
 * />
 *
 * // Without verification
 * <PhoneInput
 *   label="Phone Number"
 *   value={phoneData}
 *   onChange={setPhoneData}
 *   needsVerification={false}
 * />
 * ```
 */
export const PhoneInput = forwardRef<HTMLDivElement, PhoneInputProps>(
  (
    {
      value: userValue = { value: "", isVerified: false },
      variant = "outlined",
      onChange,
      fullWidth = true,
      needsVerification = true,
      invite,
      errorText,
      helperText,
      sx,
      ...rest
    },
    ref
  ) => {
    const [showVerificationModal, setShowVerificationModal] = useState(false);
    const [verificationCode, setVerificationCode] = useState("");
    const [verifiedNumbers, setVerifiedNumbers] = useState<Set<string>>(
      new Set()
    );
    const [verificationError, setVerificationError] = useState<string>("");
    const [sendCodeError, setSendCodeError] = useState<string>("");
    const [countdownInitialSeconds, setCountdownInitialSeconds] = useState(30);
    const { countdown, startTimer } = useCountdownTimer(
      countdownInitialSeconds
    );
    const trpc = useTRPC();
    const value = useMemo(
      () => String(userValue.value ?? ""),
      [userValue.value]
    );

    const formattedValue = useMemo(() => {
      return new AsYouType("CA").input(value);
    }, [value]);

    const isValid = useMemo(() => {
      return needsVerification ? value && isValidPhoneNumber(value) : true;
    }, [value, needsVerification]);

    // Normalize phone number to E.164 format for consistent comparison
    const normalizedValue = useMemo(() => {
      if (!isValid) return "";
      try {
        return parsePhoneNumberWithError(value, "CA").format("E.164");
      } catch {
        return "";
      }
    }, [value, isValid]);

    const showVerifyButton = Boolean(
      needsVerification && isValid && !userValue.isVerified
    );
    const incompleteInvite = Boolean(invite && (!invite.code || !invite.token));
    const incompleteInviteMessage =
      "This invitation link is missing information needed to verify a phone number. Open the full link from your invitation email, or leave the phone blank and verify it later in Edit Profile.";

    // Update verified numbers buffer whenever the value changes and is verified
    useEffect(() => {
      if (userValue.isVerified && normalizedValue) {
        setVerifiedNumbers((prev) => {
          const newSet = new Set(prev);
          newSet.add(normalizedValue);
          return newSet;
        });
      }
    }, [normalizedValue, userValue.isVerified]);

    // Mock mutation for sending verification code
    const mutation = useMutation(
      trpc.auth.verifyPhoneNumber.mutationOptions({
        onSuccess: (_, vars) => {
          setSendCodeError("");
          setVerificationError("");
          setCountdownInitialSeconds(30);
          if (vars.type === "verify-code") {
            setShowVerificationModal(false);
            setVerificationCode("");
            // Add to verified numbers buffer
            if (normalizedValue) {
              setVerifiedNumbers((prev) => new Set(prev).add(normalizedValue));
            }
            onChange({ value, isVerified: true });
          }
        },
        onSettled: (_, __, vars) => {
          if (vars.type === "send-code") {
            startTimer();
          }
        },
        onError: (error, vars) => {
          setCountdownInitialSeconds(0);
          if (vars.type === "send-code") {
            if (error.data?.code === "BAD_REQUEST") {
              setSendCodeError("Invalid phone number.");
            } else if (error.data?.code === "UNAUTHORIZED") {
              setSendCodeError(
                "Session expired. Please refresh the page and try again. If you have been sent a link, it may have expired."
              );
            } else {
              console.error("Error sending verification code:", error);
              setSendCodeError("An unknown error occurred. Please try again.");
            }
          } else {
            if (error.data?.code === "BAD_REQUEST") {
              setVerificationError("Invalid verification code.");
            } else if (error.data?.code === "UNAUTHORIZED") {
              setVerificationError(
                "Session expired. Please refresh the page and try again. If you have been sent a link, it may have expired."
              );
            } else {
              console.error("Error verifying code:", error);
              setVerificationError(
                "An unknown error occurred. Please try again."
              );
            }
          }
        },
      })
    );

    const handleVerifyClick = () => {
      if (incompleteInvite) return;
      setShowVerificationModal(true);
      mutation.mutate({
        type: "send-code",
        phone: normalizedValue,
        invite,
      });
    };

    const handleVerifyCode = () => {
      if (!verificationCode) {
        setVerificationError("Please enter the verification code.");
        return;
      }
      setVerificationError("");
      mutation.mutate({
        type: "verify-code",
        code: verificationCode,
        phone: normalizedValue,
        invite,
      });
    };

    const handleResendCode = () => {
      mutation.mutate({
        type: "send-code",
        phone: normalizedValue,
        invite,
      });
    };

    const handleCloseModal = () => {
      setShowVerificationModal(false);
      setVerificationCode("");
      setVerificationError("");
      setSendCodeError("");
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;

      // Check if this number was previously verified
      let isNumberVerified = false;
      if (isValidPhoneNumber(newValue)) {
        try {
          const normalized = parsePhoneNumberWithError(newValue, "CA").format(
            "E.164"
          );
          isNumberVerified = verifiedNumbers.has(normalized);
        } catch {
          // Invalid number, not verified
        }
      }

      onChange({
        value: newValue,
        isVerified: isNumberVerified,
      });
    };

    useEffect(() => {
      if (userValue.isVerified) {
        setShowVerificationModal(false);
        setVerificationCode("");
      }
    }, [userValue.isVerified]);

    const formControlProps = {
      disabled: rest.disabled,
      color: rest.color,
      error: rest.error,
      focused: rest.focused,
      fullWidth,
      hiddenLabel: rest.hiddenLabel,
      margin: rest.margin,
      size: rest.size,
      variant,
      required: rest.required,
    };

    return (
      <>
        <FormControl
          sx={mergeSx({ maxWidth: "100%" }, sx)}
          ref={ref}
          {...formControlProps}
        >
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              maxWidth: "100%",
            }}
          >
            <Input
              {...rest}
              fullWidth={fullWidth}
              variant={variant}
              value={formattedValue}
              onChange={handleInputChange}
              type="tel"
              autoComplete="mobile tel"
              inputMode="tel"
              onKeyDown={(e) => {
                rest.onKeyDown?.(e);
                if (e.key === "Enter" && showVerifyButton) {
                  e.preventDefault();
                  handleVerifyClick();
                }
              }}
            />
            <Collapse in={showVerifyButton} orientation="horizontal">
              <Button
                onClick={handleVerifyClick}
                size="small"
                variant="text"
                disabled={incompleteInvite}
              >
                Verify
              </Button>
            </Collapse>
            <Collapse
              in={needsVerification && userValue.isVerified}
              orientation="horizontal"
              sx={{
                "& .MuiCollapse-wrapperInner": {
                  height: "initial",
                },
              }}
            >
              <CheckCircleIcon
                color="success"
                sx={{
                  height: "100%",
                  alignSelf: "center",
                  ml: 1,
                }}
                data-testid="phone-verified-icon"
              />
            </Collapse>
          </Stack>
          <FormHelperText sx={{ mt: -1 }}>
            {incompleteInvite
              ? incompleteInviteMessage
              : errorText || helperText}
          </FormHelperText>
        </FormControl>

        {showVerificationModal && (
          <Modal
            open={showVerificationModal}
            onClose={handleCloseModal}
            header="Verify Phone Number"
            maxWidth="sm"
          >
            <Typography>
              A verification code has been sent to{" "}
              <strong>{formattedValue}</strong>.
            </Typography>
            <Typography>
              Please enter it below to verify your phone number.
            </Typography>

            {sendCodeError && (
              <Alert severity="error" sx={{ mt: 2, width: "100%" }}>
                <AlertTitle sx={{ fontWeight: 600 }}>
                  Error sending verification code
                </AlertTitle>
                {sendCodeError}
              </Alert>
            )}

            <Input
              data-testid="verification-code-input"
              label="Verification Code"
              value={verificationCode}
              onChange={(e) => {
                setVerificationCode(e.target.value);
                if (verificationError) setVerificationError("");
              }}
              placeholder="Enter 6-digit code"
              error={!!verificationError}
              errorText={verificationError}
              slotProps={{
                htmlInput: {
                  maxLength: 6,
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                },
              }}
              disabled={mutation.isPending}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter" && verificationCode.length === 6) {
                  e.preventDefault();
                  handleVerifyCode();
                }
              }}
            />
            <DialogActions>
              <Stack direction="row" gap={1} sx={{ width: "100%" }}>
                <Button
                  data-testid="verify-code-button"
                  onClick={handleVerifyCode}
                  disabled={verificationCode.length !== 6}
                  loading={mutation.isPending}
                  fullWidth
                >
                  Verify Code
                </Button>
                <Button
                  data-testid="resend-code-button"
                  onClick={handleResendCode}
                  disabled={countdown > 0 || mutation.isPending}
                  loading={mutation.isPending}
                  variant="outlined"
                  fullWidth
                >
                  {countdown > 0 ? `Resend (${countdown}s)` : "Resend"}
                </Button>
              </Stack>
            </DialogActions>
          </Modal>
        )}
      </>
    );
  }
);
